import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import type { IdToken, User } from '@auth0/auth0-spa-js';
import { clearFeatureFlagOverrideStorage } from '@quipex/core/config';
import { jwtDecode } from 'jwt-decode';
import {
    combineLatest,
    filter,
    firstValueFrom,
    map,
    Subject,
    take,
    type Observable,
} from 'rxjs';

export interface AuthenticatedAccount {
    sub: string;
    name: string;
    email?: string;
}

export interface LoginOptions {
    connection?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    //#region Dependency injection
    private readonly auth0Service = inject(Auth0Service);
    //#endregion

    //#region State
    private readonly authUser = toSignal(this.auth0Service.user$, {
        initialValue: null,
    });
    private readonly idTokenClaims = toSignal(
        this.auth0Service.idTokenClaims$,
        {
            initialValue: null,
        }
    );
    public readonly activeAccount = computed<AuthenticatedAccount | null>(() =>
        buildAuthenticatedAccount(this.authUser(), this.idTokenClaims())
    );

    public readonly isLoading$ = this.auth0Service.isLoading$;
    public readonly isAuthenticated$ = this.auth0Service.isAuthenticated$;
    public readonly error$ = this.auth0Service.error$;
    private readonly logoutStarted = new Subject<void>();
    public readonly logoutStarted$ = this.logoutStarted.asObservable();
    //#endregion

    //#region Public methods
    public initialize() {
        return combineLatest([this.isLoading$, this.isAuthenticated$]).pipe(
            filter(([isLoading]) => !isLoading),
            map(([, isAuthenticated]) => isAuthenticated),
            take(1)
        );
    }

    public enableAccountStorageEvents(): void {
        // Auth0 SPA SDK does not expose an MSAL-style storage event API.
    }

    public login(targetPath = '/', options: LoginOptions = {}): void {
        this.auth0Service
            .loginWithRedirect({
                appState: {
                    target: targetPath,
                },
                authorizationParams: options.connection
                    ? {
                          connection: options.connection,
                      }
                    : undefined,
            })
            .subscribe();
    }

    public async getAccessToken(): Promise<string> {
        return firstValueFrom(this.auth0Service.getAccessTokenSilently());
    }

    public getAccessTokenPayload(): Observable<Record<string, unknown>> {
        return this.auth0Service
            .getAccessTokenSilently()
            .pipe(map((token) => decodeJwtPayload(token)));
    }

    public logout(): void {
        this.logoutStarted.next();
        clearFeatureFlagOverrideStorage();
        this.auth0Service
            .logout({
                logoutParams: { returnTo: window.location.origin },
            })
            .subscribe();
    }
    //#endregion
}

function buildAuthenticatedAccount(
    user: User | null | undefined,
    claims: IdToken | null | undefined
): AuthenticatedAccount | null {
    if (!user) {
        return null;
    }

    const email = resolveEmail(user, claims);

    return {
        sub: user.sub ?? email ?? 'unknown',
        name: resolveDisplayName(user, email),
        email,
    };
}

function resolveEmail(
    user: User,
    claims: IdToken | null | undefined
): string | undefined {
    if (user.email) return user.email;
    if (!claims) return undefined;

    // Azure AD / enterprise connection common claim locations
    const candidates: unknown[] = [
        claims['preferred_username'],
        claims['upn'],
        claims['unique_name'],
        claims['email'],
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.includes('@')) {
            return candidate;
        }
    }

    // Scan for any namespaced claim whose key ends with /email or :email
    for (const [key, value] of Object.entries(claims)) {
        if (
            (key.endsWith('/email') || key.endsWith(':email')) &&
            typeof value === 'string' &&
            value.includes('@')
        ) {
            return value;
        }
    }

    return undefined;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
    return jwtDecode<Record<string, unknown>>(token);
}

function resolveDisplayName(user: User, email: string | undefined): string {
    const rawName = user.name ?? user.nickname ?? email ?? 'Unknown';
    const atIndex = rawName.indexOf('@');
    return atIndex === -1 ? rawName : rawName.slice(0, atIndex);
}
